// Story 1.31 — TanStack Query hook for GET /api/v1/admin/moderation/flags.
//
// Powers the admin moderation queue list. Refetch on window focus so
// the admin can glance away + return without manually refreshing;
// post-action invalidation handles the explicit refresh path.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ModerationFlagStatus = "pending" | "actioned" | "dismissed" | "any";

export type ModerationFlagsFilters = {
  status: ModerationFlagStatus;
  page: number;
  page_size: number;
};

export const DEFAULT_MODERATION_FILTERS: ModerationFlagsFilters = {
  status: "pending",
  page: 1,
  page_size: 50,
};

export type ModerationFlagTargetKind =
  | "creator_image"
  | "hello_text"
  | "share_certificate"
  | "bulletin_post";

export type ModerationFlagListItem = {
  id: string;
  target_kind: ModerationFlagTargetKind;
  target_id: string;
  status: "pending" | "actioned" | "dismissed";
  reason: string | null;
  created_at: string;
  reviewed_at: string | null;
  action_taken: string | null;
  creator_display_name: string | null;
  preview_signed_url: string | null;
  preview_signed_url_expires_at: string | null;
  hello_text_preview: string | null;
  flagger_label: string;
  flagged_by_user_id: string | null;
};

export type ModerationFlagListResponse = {
  data: ModerationFlagListItem[];
  page: number;
  page_size: number;
  has_next: boolean;
};

function buildQueryString(filters: ModerationFlagsFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  params.set("page", String(filters.page));
  params.set("page_size", String(filters.page_size));
  return params.toString();
}

async function fetchModerationFlags(
  filters: ModerationFlagsFilters
): Promise<ModerationFlagListResponse> {
  const qs = buildQueryString(filters);
  const response = await fetch(`/api/v1/admin/moderation/flags?${qs}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin moderation flags fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as ModerationFlagListResponse;
}

export function useAdminModerationFlags(filters: ModerationFlagsFilters) {
  return useQuery<ModerationFlagListResponse, Error>({
    queryKey: ["admin-moderation-flags", filters],
    queryFn: () => fetchModerationFlags(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
